import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

const AUTH_TOKEN_KEY = "admin_auth_token";

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if there's a valid token on mount and verify it with backend
    const verifyToken = async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);

      if (token) {
        try {
          // Verify token with backend
          await api.post('/admin/auth/verify', {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setIsAuthenticated(true);
        } catch (error) {
          // Token invalid or expired, clear storage
          localStorage.removeItem(AUTH_TOKEN_KEY);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (email, password) => {
    try {
      // Note: api interceptor already returns response.data
      const response = await api.post('/admin/auth/login', {
        email,
        password
      });

      if (response.success) {
        // Store JWT token
        localStorage.setItem(AUTH_TOKEN_KEY, response.token);
        setIsAuthenticated(true);
        return { success: true };
      }

      return { success: false, error: "Login failed" };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || "Invalid email or password"
      };
    }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
